import React, { useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import UploadButton from "../UploadButton/UploadButton";
import { useSelector } from "react-redux";
import { parseJSON } from "../../utils/helpers/universalFunctions";

// Worker setup (VERY IMPORTANT)
pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

// AST /ALT Ratio
//  "urea": "4.86"

// 🔹 Your fields config

// 🔹 Normalize text
const normalizeText = (text) => {
  return text
    .replace(/\s+/g, " ")
    .replace(/[\uFFFD]/g, "")
    .trim();
};

const TEXT_RESULTS_REGEX = /^(non[-\s]?reactive|reactive|positive|negative)$/i;

// 🔹 Default aliases for tests that commonly appear under alternate names in lab PDFs
const DEFAULT_ALIASES = {
  vitamin_b12: [
    "Vitamin B12 / Cobalamin",
    "Vitamin B12/Cobalamin",
    "Vitamin B12",
    "Cobalamin",
    "Vitamin B-12",
  ],
  vitamin_d: [
    "Vitamin D 25 - Hydroxy",
    "Vitamin D 25-Hydroxy",
    "Vitamin D (25-OH)",
    "25-Hydroxy Vitamin D",
    "Vitamin D (Hydroxy)",
    "Vitamin D Total",
    "Vitamin D",
    "25 - OH Vitamin D",
    "25-OH Vitamin D",
  ],
  vitamin_d_hydroxy: [
    "Vitamin D 25 - Hydroxy",
    "Vitamin D 25-Hydroxy",
    "Vitamin D (25-OH)",
    "25-Hydroxy Vitamin D",
    "Vitamin D (Hydroxy)",
    "Vitamin D Total",
    "Vitamin D",
    "25 - OH Vitamin D",
    "25-OH Vitamin D",
  ],
  ggt: [
    "GGT",
    "Gamma-Glutamyl Transferase (GGT)",
    "Gamma Glutamyl Transferase (GGT)",
    "Gamma-Glutamyl Transferase",
    "Gamma Glutamyl Transferase",
    "GGTP",
  ],
  gamma_glutamyl_transferase_ggt: [
    "GGT",
    "Gamma-Glutamyl Transferase (GGT)",
    "Gamma Glutamyl Transferase (GGT)",
    "Gamma-Glutamyl Transferase",
    "Gamma Glutamyl Transferase",
    "GGTP",
  ],
  phosphorus: [
    "Phosphorus Serum",
    "Phosphorus, Serum",
    "Phosphorus - Serum",
    "Serum Phosphorus",
    "Phosphorus",
    "Phosphate",
    "Inorganic Phosphorus",
  ],
  phosphorus_serum: [
    "Phosphorus Serum",
    "Phosphorus, Serum",
    "Phosphorus - Serum",
    "Serum Phosphorus",
    "Phosphorus",
    "Phosphate",
    "Inorganic Phosphorus",
  ],
  esr: [
    "Erythrocyte Sedimentation Rate (ESR)",
    "Erythrocyte Sedimentation Rate",
    "ESR",
  ],
  erythrocyte_sedimentation_rate_esr: [
    "Erythrocyte Sedimentation Rate (ESR)",
    "Erythrocyte Sedimentation Rate",
    "ESR",
  ],
  hba1c: [
    "HbA1c",
    "HbA1C (Glycosylated Hemoglobin)",
    "Glycosylated Hemoglobin",
    "HBA1C",
  ],
  fasting_plasma_glucose: [
    "Fasting Plasma Glucose",
    "Fasting Blood Sugar",
    "Glucose Fasting",
    "Blood Sugar Fasting",
  ],
  mean_plasma_glucose: [
    "Mean Plasma Glucose",
    "Estimated Average Glucose (eAG)",
  ],
  total_cholesterol: [
    "Total Cholesterol",
    "Cholesterol - Total",
    "Cholesterol Total",
  ],
  triglycerides: ["Triglycerides", "Serum Triglycerides"],
  hdl_cholesterol: [
    "HDL Cholesterol",
    "HDL - Cholesterol",
    "High Density Lipoprotein",
  ],
  ldl_cholesterol: [
    "LDL Cholesterol (Calculated)",
    "LDL Cholesterol",
    "LDL - Cholesterol",
  ],
  vldl_cholesterol: ["VLDL Cholesterol", "VLDL - Cholesterol"],
  total_cholesterol_hdl_ratio: [
    "Total Cholesterol / HDL Ratio",
    "Cholesterol / HDL Ratio",
    "Total Cholesterol/HDL Ratio",
  ],
  ldl_hdl_ratio: ["LDL / HDL Ratio", "LDL/HDL Ratio"],
  non_hdl_cholesterol: ["Non HDL Cholesterol", "Non-HDL Cholesterol"],
  bilirubin_total: ["Bilirubin Total", "Total Bilirubin", "Bilirubin, Total"],
  bilirubin_direct: ["Bilirubin Direct", "Direct Bilirubin", "Bilirubin, Direct"],
  serum_bilirubin_indirect: [
    "Serum Bilirubin (Indirect)",
    "Indirect Bilirubin",
    "Bilirubin, Indirect",
  ],
  sgot: ["SGOT / AST", "SGOT", "AST", "AST / SGOT", "SGOT/AST"],
  sgot_ast: ["SGOT / AST", "SGOT", "AST", "AST / SGOT", "SGOT/AST"],
  sgpt: ["SGPT / ALT", "SGPT", "ALT", "ALT / SGPT", "SGPT/ALT"],
  sgpt_alt: ["SGPT / ALT", "SGPT", "ALT", "ALT / SGPT", "SGPT/ALT"],
  ast_alt_ratio: ["AST / ALT Ratio", "AST/ALT Ratio", "SGOT/SGPT Ratio"],
  alkaline_phosphatase: [
    "Alkaline Phosphatase (ALP)",
    "Alkaline Phosphatase",
    "ALP",
  ],
  alp: ["Alkaline Phosphatase (ALP)", "Alkaline Phosphatase", "ALP"],
  total_protein: ["Total Protein", "Protein Total", "Serum Total Protein"],
  albumin: ["Albumin", "Serum Albumin"],
  globulin: ["Globulin", "Serum Globulin"],
  albumin_globulin_ratio: [
    "Albumin Globulin A/G Ratio",
    "Albumin Globulin Ratio",
    "A/G Ratio",
  ],
  blood_urea_nitrogen: ["Blood Urea Nitrogen", "BUN"],
  blood_urea: ["Blood Urea", "Urea"],
  creatinine: ["Creatinine", "Serum Creatinine"],
  bun_creatinine_ratio: [
    "BUN Creatinine Ratio",
    "BUN/Creatinine Ratio",
    "BUN / Creatinine Ratio",
  ],
  uric_acid: ["Uric Acid", "Serum Uric Acid"],
  sodium: ["Sodium", "Serum Sodium"],
  potassium: ["Potassium", "Serum Potassium"],
  chloride: ["Chloride", "Serum Chloride"],
  calcium: ["Calcium", "Serum Calcium", "Total Calcium"],
  magnesium: ["Magnesium", "Serum Magnesium"],
  high_sensitivity_c_reactive_protein_hs_crp: [
    "High-Sensitivity C-Reactive Protein(hs-CRP)",
    "High-Sensitivity C-Reactive Protein",
    "hs-CRP",
    "hsCRP",
  ],
  apolipoprotein_b: ["Apolipoprotein B", "Apo B"],
  ferritin: ["Ferritin", "Serum Ferritin"],
  total_t3: [
    "Total T3 (Triiodothyronine)",
    "Total T3",
    "T3 Total",
    "Triiodothyronine",
  ],
  total_t4: ["Total T4 (Thyroxine)", "Total T4", "T4 Total", "Thyroxine"],
  tsh: [
    "TSH 3rd Generation",
    "TSH",
    "Thyroid Stimulating Hormone",
    "TSH Ultrasensitive",
  ],
  haemoglobin: ["Haemoglobin (Hb)", "Haemoglobin", "Hemoglobin", "Hb"],
  total_wbc_count: [
    "Total WBC Count / TLC",
    "Total WBC Count",
    "TLC",
    "Total Leucocyte Count",
  ],
  neutrophils: ["Neutrophils"],
  lymphocytes: ["Lymphocytes"],
  eosinophils: ["Eosinophils"],
  monocytes: ["Monocytes"],
  basophils: ["Basophils"],
  absolute_neutrophil_count: [
    "Absolute Neutrophil Count (ANC)",
    "Absolute Neutrophil Count",
    "ANC",
  ],
  absolute_lymphocyte_count: [
    "Absolute Lymphocyte Count",
    "ALC",
  ],
  absolute_eosinophil_count: [
    "Absolute Eosinophil Count (AEC)",
    "Absolute Eosinophil Count",
    "AEC",
  ],
  absolute_monocyte_count: [
    "Absolute Monocyte Count",
    "AMC",
  ],
  absolute_basophil_count: [
    "Absolute Basophil Count",
    "ABC",
  ],
  rbc_count: ["RBC Count", "Total RBC Count", "RBC"],
  pcv_hematocrit: ["PCV / Hematocrit", "PCV", "Hematocrit"],
  mcv: ["MCV"],
  mch: ["MCH"],
  mchc: ["MCHC"],
  rdw: ["RDW (Red Cell Distribution Width)", "RDW"],
  platelet_count: ["Platelet Count"],
  mpv: ["MPV (Mean Platelet Volume)", "MPV"],
};

/**
 * 🔹 Clean Result values (handling flags like 49.00 H, 35.80 L, text results, etc.)
 */
const cleanResultValue = (str) => {
  if (!str) return "";
  const trimmed = str.trim();

  // 1. Text results (non-reactive, reactive, positive, negative)
  if (TEXT_RESULTS_REGEX.test(trimmed)) {
    return trimmed;
  }

  // 2. Comparison values like "< 0.1" or "> 100"
  const compMatch = trimmed.match(/^[<>]\s*\d+(?:\.\d+)?/);
  if (compMatch) {
    return compMatch[0].replace(/\s+/g, "");
  }

  // 3. Numeric values with optional flag (e.g., "49.00 H", "35.80 L", "12.10")
  const numMatch = trimmed.match(/^(\d+(?:\.\d+)?)\s*[HL]?/i);
  if (numMatch) {
    return numMatch[1];
  }

  // 4. Any numeric token
  const anyNum = trimmed.match(/\d+(?:\.\d+)?/);
  if (anyNum) {
    return anyNum[0];
  }

  return trimmed;
};

/**
 * 🔹 Detect table columns from header items on the page
 * Columns: Test Name | Result | Biological Ref. Interval | Unit
 */
const detectTableColumns = (items) => {
  let testNameItem = items.find((it) => /Test\s*Name/i.test(it.str.trim()));
  if (!testNameItem) {
    testNameItem = items.find((it) => /^Test$/i.test(it.str.trim()));
  }

  const resultItem = items.find(
    (it) => /Result/i.test(it.str.trim())
  );
  const refIntervalItem = items.find((it) =>
    /Biological|Ref\.\s*Interval|Ref\s*Interval|Reference/i.test(it.str.trim())
  );
  const unitItem = items.find((it) => /^Unit$/i.test(it.str.trim()));

  if (resultItem) {
    const headerY = resultItem.transform[5];
    const x0 = testNameItem ? testNameItem.transform[4] : 40;
    const x1 = resultItem.transform[4];
    const x2 = refIntervalItem ? refIntervalItem.transform[4] : x1 + 110;
    const x3 = unitItem ? unitItem.transform[4] : x2 + 130;

    return {
      headerY,
      boundaries: {
        col0End: x1 - 15,
        col1End: x2 - 15,
        col2End: x3 - 15,
      },
    };
  }

  return null;
};

/**
 * 🔹 Extract structured table rows from page items using column boundaries
 */
const extractPageTableRows = (items, boundaries, headerY, pageNum) => {
  const yTop = headerY ? headerY - 4 : 750;

  // Filter items in the table region
  const tableItems = items.filter((it) => {
    const y = it.transform[5];
    const str = it.str.trim();
    if (!str) return false;
    if (headerY && y >= yTop) return false; // Skip header and patient info
    if (y < 45) return false;               // Skip footer
    if (/^Page\s*No:/i.test(str)) return false;
    if (/^Authenticated\s*by/i.test(str)) return false;
    return true;
  });

  // Group items by vertical position (Y-coordinate)
  const linesByY = {};
  tableItems.forEach((item) => {
    const y = Math.round(item.transform[5]);
    const existingKey = Object.keys(linesByY).find((k) => Math.abs(Number(k) - y) <= 4);
    if (existingKey) {
      linesByY[existingKey].push(item);
    } else {
      linesByY[y] = [item];
    }
  });

  // Sort descending by Y (top of page to bottom)
  const sortedY = Object.keys(linesByY).sort((a, b) => Number(b) - Number(a));

  // Partition each visual line into the 4 columns
  const rowLines = sortedY.map((yKey) => {
    const lineItems = linesByY[yKey].sort((a, b) => a.transform[4] - b.transform[4]);
    const cols = ["", "", "", ""];
    lineItems.forEach((it) => {
      const x = it.transform[4];
      let colIdx = 0;
      if (x < boundaries.col0End) colIdx = 0;
      else if (x < boundaries.col1End) colIdx = 1;
      else if (x < boundaries.col2End) colIdx = 2;
      else colIdx = 3;

      cols[colIdx] = cols[colIdx]
        ? `${cols[colIdx]} ${it.str.trim()}`.trim()
        : it.str.trim();
    });

    return {
      y: Number(yKey),
      col0: cols[0], // Test Name
      col1: cols[1], // Result
      col2: cols[2], // Biological Ref. Interval
      col3: cols[3], // Unit
    };
  });

  // Assemble into logical test table rows
  const tableRows = [];
  let pendingTestName = "";

  rowLines.forEach((line) => {
    let { col0, col1, col2, col3 } = line;

    // Check if result leaked into col0 (e.g., "Sample : Serum 622.80" with col1 empty)
    if (!col1 && col0) {
      const splitMatch = col0.match(/^(.*?)\s+((?:[<>]\s*)?\d+(?:\.\d+)?\s*[HL]?)$/i);
      if (splitMatch) {
        col0 = splitMatch[1].trim();
        col1 = splitMatch[2].trim();
      }
    }

    const isCol0Metadata =
      /^(Sample|Specimen|Method|MC\s*-)\s*:/i.test(col0) ||
      /^MC\s*-\s*\d+/i.test(col0);

    const cleanVal = cleanResultValue(col1);

    if (cleanVal) {
      let testName = "";

      if (col0 && !isCol0Metadata) {
        if (pendingTestName && /^\(.*\)$/.test(col0)) {
          testName = `${pendingTestName} ${col0}`.trim();
        } else {
          testName = col0.trim();
        }
      } else if (pendingTestName) {
        // Result is on a line where col0 is "Sample : Serum" or empty
        testName = pendingTestName.trim();
      }

      if (testName) {
        tableRows.push({
          testName: testName.trim(),
          result: cleanVal,
          rawResult: col1,
          refInterval: col2.trim(),
          unit: col3.trim(),
          page: pageNum,
        });
        pendingTestName = "";
      }
    } else {
      // Line without result
      if (col0 && !isCol0Metadata) {
        if (pendingTestName) {
          if (/^\(.*\)$/.test(col0)) {
            pendingTestName = `${pendingTestName} ${col0}`.trim();
          } else {
            pendingTestName = col0.trim();
          }
        } else {
          pendingTestName = col0.trim();
        }
      }
      // If isCol0Metadata is true (e.g. "Sample : Serum", "Method : ..."),
      // pendingTestName is preserved so the upcoming result can be linked to it!
    }
  });

  return { tableRows, rowLines };
};

/**
 * 🔹 Helper to get all possible aliases for a field based on label and key
 */
// const getAliasesForField = (label, key) => {
//   const aliases = new Set();
//   if (label) aliases.add(label);

//   const cleanKey = (key || "").toLowerCase();
//   const cleanLabel = (label || "").toLowerCase();

//   // If exact key in DEFAULT_ALIASES
//   if (DEFAULT_ALIASES[cleanKey]) {
//     DEFAULT_ALIASES[cleanKey].forEach((a) => aliases.add(a));
//   }

//   // Vitamin D
//   if (/vitamin\s*d/i.test(cleanKey) || /vitamin\s*d/i.test(cleanLabel)) {
//     (DEFAULT_ALIASES.vitamin_d || []).forEach((a) => aliases.add(a));
//   }

//   // Vitamin B12 / Cobalamin
//   if (/b12|cobalamin/i.test(cleanKey) || /b12|cobalamin/i.test(cleanLabel)) {
//     (DEFAULT_ALIASES.vitamin_b12 || []).forEach((a) => aliases.add(a));
//   }

//   // GGT / Gamma Glutamyl
//   if (/ggt|glutamyl/i.test(cleanKey) || /ggt|glutamyl/i.test(cleanLabel)) {
//     (DEFAULT_ALIASES.ggt || []).forEach((a) => aliases.add(a));
//   }

//   // Phosphorus
//   if (/phosphor|phosphat/i.test(cleanKey) || /phosphor|phosphat/i.test(cleanLabel)) {
//     (DEFAULT_ALIASES.phosphorus || []).forEach((a) => aliases.add(a));
//   }

//   // ESR
//   if (/esr|erythrocyte/i.test(cleanKey) || /esr|erythrocyte/i.test(cleanLabel)) {
//     (DEFAULT_ALIASES.esr || []).forEach((a) => aliases.add(a));
//   }

//   // HbA1c
//   if (/hba1c|glycated|glycosylated/i.test(cleanKey) || /hba1c|glycated|glycosylated/i.test(cleanLabel)) {
//     (DEFAULT_ALIASES.hba1c || []).forEach((a) => aliases.add(a));
//   }

//   return Array.from(aliases);
// };

/**
 * 🔹 Extract field values directly from the column-structured table rows
 */
const extractValuesFromTable = (tableRows, fields) => {
  const result = [];
  const matchedFieldKeys = new Set();

  fields.forEach((item) => {
    const label = item.test_name;
    const key = item.key_name;
    const dbAliases = parseJSON(item.aliases) || [];
    // const helperAliases = getAliasesForField(label, key);
    const allLabels = Array.from(
      new Set([label, ...dbAliases].filter(Boolean))
    );

    let matchedRow = null;

    for (const lbl of allLabels) {
      const cleanLbl = lbl.trim().toLowerCase();
      const normLbl = cleanLbl.replace(/[^a-z0-9]/g, "");

      // 1. Exact match (case insensitive or normalized)
      matchedRow = tableRows.find((row) => {
        const cleanRowName = row.testName.trim().toLowerCase();
        const normRow = cleanRowName.replace(/[^a-z0-9]/g, "");
        return cleanRowName === cleanLbl || normRow === normLbl;
      });
      if (matchedRow) break;

      // 2. Starts with / includes match with collision guards
      matchedRow = tableRows.find((row) => {
        const cleanRowName = row.testName.trim().toLowerCase();
        const normRow = cleanRowName.replace(/[^a-z0-9]/g, "");

        // Guards against partial sub-string conflicts
        if (normLbl === "bloodurea" && normRow.includes("nitrogen")) return false;
        if (normLbl === "totalprotein" && (normRow.includes("albumin") || normRow.includes("ag"))) return false;
        if (normLbl === "bilirubintotal" && normRow.includes("direct")) return false;
        if (normLbl === "totalcholesterol" && normRow.includes("hdl")) return false;

        return (
          normRow === normLbl ||
          normRow.startsWith(normLbl) ||
          normLbl.startsWith(normRow) ||
          (normLbl.length >= 4 && normRow.includes(normLbl)) ||
          (normRow.length >= 4 && normLbl.includes(normRow))
        );
      });
      if (matchedRow) break;
    }

    if (matchedRow && matchedRow.result) {
      matchedFieldKeys.add(key);
      result.push({
        label,
        key,
        unit: item.unit || matchedRow.unit || "",
        order_by: item.order_by,
        value: matchedRow.result,
      });
    }
  });

  return { result, matchedFieldKeys };
};

/**
 * 🔹 Fallback line-based value extractor for any fields not matched by table columns
 */
const extractLabValuesFromLines = (lines, fields) => {
  lines = lines.map((line) => {
    let cleaned = normalizeText(line);
    // Strip sample metadata like "Sample : Serum" if it occurs before the test value
    cleaned = cleaned.replace(
      /\b(?:Sample|Specimen)\s*:\s*[A-Za-z\s/]+?(?=\s[\d<>]|\s*(?:non[-\s]?reactive|reactive|positive|negative))/gi,
      ""
    );
    return cleaned.trim();
  });

  const result = [];

  fields.forEach((item) => {
    let label = item.test_name;
    let key = item.key_name;
    let alias = parseJSON(item.aliases) || [];
    // let defaultAlias = getAliasesForField(label, key);
    const allLabels = Array.from(
      new Set([label, ...alias])
    ).sort((a, b) => b.length - a.length);

    let value = "";

    for (let line of lines) {
      for (let lbl of allLabels) {
        const safeLabel = lbl.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

        const regex = new RegExp(
          `^${safeLabel}(?!\\s*[A-Za-z\\/|:-])([\\s\\S]{0,100})`,
          "i"
        );

        const match = line.match(regex);

        if (match) {
          const afterText = match[1];
          const tokens = afterText.split(/\s+/).filter(Boolean);

          for (let i = 0; i < tokens.length; i++) {
            const single = tokens[i];
            const combined = `${tokens[i]} ${tokens[i + 1] || ""}`.trim();

            if (TEXT_RESULTS_REGEX.test(single)) {
              value = single;
              break;
            }

            if (TEXT_RESULTS_REGEX.test(combined)) {
              value = combined;
              break;
            }
          }

          if (!/[a-zA-Z]/.test(tokens[0])) {
            if (!value) {
              for (let i = 0; i < tokens.length; i++) {
                const token = tokens[i];

                if (/^\d+(\.\d+)?\s*[HL]?$/.test(token)) {
                  const next = tokens[i + 1];
                  const nextNext = tokens[i + 2];

                  if (next === "-" && /^\d+(\.\d+)?$/.test(nextNext)) {
                    continue;
                  }

                  value = token.replace(/[^\d.]/g, "");
                  break;
                }
              }
            }
          }
        }

        if (value) break;

        if (lbl === "High-Sensitivity C-Reactive Protein(hs-") {
          let indexOfLabel = lines.indexOf(
            "High-Sensitivity C-Reactive Protein(hs-"
          );
          if (
            indexOfLabel !== -1 &&
            lines[indexOfLabel + 1] &&
            /^\d+(\.\d+)?\s*[HL]?$/.test(
              lines[indexOfLabel + 1]?.split(" ")[0]
            )
          ) {
            value = lines[indexOfLabel + 1]?.split(" ")[0];
            break;
          }
        }
      }
      if (value) break;
    }

    if (value) {
      result.push({
        label,
        key,
        unit: item.unit || "",
        order_by: item.order_by,
        value: value || "",
      });
    }
  });

  return result;
};

const mergeBrokenLines = (lines) => {
  const merged = [];

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];

    if (/hbsag|hepatitis b/i.test(line)) {
      let combined = line;

      for (let j = i + 1; j <= i + 5; j++) {
        if (!lines[j]) break;

        combined += " " + lines[j];

        if (/non reactive|reactive|\d/.test(lines[j])) {
          i = j;
          break;
        }
      }

      merged.push(combined);
      continue;
    }

    // Merge lines where the next line contains Sample/Specimen information and results
    if (i + 1 < lines.length && /^(Sample|Specimen)\s*:/i.test(lines[i + 1])) {
      let combined = line + " " + lines[i + 1];
      i++;

      // If the result is on the line directly following Sample : ...
      if (
        i + 1 < lines.length &&
        !/\d|non[-\s]?reactive|reactive|positive|negative/i.test(combined) &&
        !/^(Sample|Specimen)\s*:/i.test(lines[i + 1])
      ) {
        combined += " " + lines[i + 1];
        i++;
      }

      merged.push(combined);
      continue;
    }

    merged.push(line);
  }

  return merged;
};

function extractIronStudies(data) {
  // Step 1: Get only Iron Studies section
  const ironSectionMatch = data.match(/Iron Studies[\s\S]*?% Saturation[\s\S]*?Calculated/);

  if (!ironSectionMatch) return "";

  const section = ironSectionMatch[0];

  // Step 2: Extract values using regex
  const patterns = [
    { name: "Iron", method: "Ferrozine", regex: /Iron\s+([\d.]+)/ },
    { name: "UIBC", method: "Direct Determination", regex: /UIBC\s+([\d.]+)/ },
    { name: "Total Iron Binding Capacity (TIBC)", method: "Calculated", regex: /Total Iron Binding Capacity \(TIBC\)\s+([\d.]+)/ },
    { name: "% Saturation", method: "Calculated", regex: /% Saturation\s+([\d.]+)/ }
  ];

  const results = patterns.map(p => {
    const match = section.match(p.regex);
    return match ? `${p.name} Method : ${p.method} ${match[1]}` : null;
  }).filter(Boolean);

  // Step 3: Return final object
  return results.join(",") + " µg/dL";
}

const DEFAULT_BOUNDARIES = { col0End: 230, col1End: 340, col2End: 490 };

const PDFExtractorOfLabTest = ({ name, files, setFiles, setContent }) => {
  const [text, setText] = useState("");
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const { staticLabTests } = useSelector(state => state.consultationNotesReducer)
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);

    try {
      const arrayBuffer = await file.arrayBuffer();
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
      const pdf = await loadingTask.promise;

      let fullText = "";
      let allTableRows = [];
      let lastKnownBoundaries = DEFAULT_BOUNDARIES;

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const items = textContent.items;

        // ============================
        // 🔹 1. DETECT TABLE COLUMNS
        // ============================
        const detected = detectTableColumns(items);
        const boundaries = detected ? detected.boundaries : lastKnownBoundaries;
        const headerY = detected ? detected.headerY : null;
        if (detected) {
          lastKnownBoundaries = detected.boundaries;
        }

        // ============================
        // 🔹 2. EXTRACT TABLE ROWS COLUMN-WISE
        // ============================
        const { tableRows, rowLines } = extractPageTableRows(
          items,
          boundaries,
          headerY,
          i
        );
        allTableRows = allTableRows.concat(tableRows);

        // ============================
        // 🔹 3. ASSEMBLE FULL TEXT FOR FALLBACK
        // ============================
        const pageText = rowLines
          .map((r) => `${r.col0} ${r.col1} ${r.col2} ${r.col3}`.trim())
          .filter(Boolean)
          .join("\n");

        fullText += "\n" + pageText;
      }

      console.log("Extracted Table Columns and Rows:", allTableRows);
      setTableData(allTableRows);

      // ============================
      // ✅ CLEAN LINES
      // ============================
      const rawLines = fullText
        .split(/\n/)
        .map((l) => l.replace(/\s+/g, " ").trim())
        .filter(Boolean);

      // 🔥 IMPORTANT: MERGE BROKEN LINES
      const lines = mergeBrokenLines(rawLines);

      setText(lines.join("\n"));

      // ============================================
      // 🔥 4. EXTRACT VALUES FROM TABLE COLUMNS FIRST
      // ============================================
      const allFields = staticLabTests.data?.flatMap(c => c.list) || [];
      const { result: tableExtracted, matchedFieldKeys } = extractValuesFromTable(allTableRows, allFields);

      // ============================================
      // 🔥 5. FALLBACK TO LINE EXTRACTION FOR REMAINING
      // ============================================
      const remainingFields = allFields.filter(f => !matchedFieldKeys.has(f.key_name));
      const fallbackExtracted = remainingFields.length > 0 ? extractLabValuesFromLines(lines, remainingFields) : [];

      const extracted = [...tableExtracted, ...fallbackExtracted];
      console.log("Extracted Field Values:", extracted);

      let iron_studies = allFields.find(item => item.key_name === "iron_studies")

      let iron_studies_value = extractIronStudies(lines.join("\n"))
      if (iron_studies && iron_studies_value) {
        let index = extracted.findIndex(item => item.key === "iron_studies")
        if (index >= 0) {
          extracted[index].value = iron_studies_value
        } else {
          extracted.push({
            label: iron_studies.test_name,
            key: iron_studies.key_name,
            unit: iron_studies.unit || "µg/dL",
            order_by: iron_studies.order_by,
            value: iron_studies_value
          })
        }
      }

      setContent(prev => {

        let updated = { ...prev }
        const existing = prev.data || [];
        const incoming = extracted || [];

        // Create map from existing data
        const map = new Map(existing.map(item => [item.label, item]));

        // Merge / replace
        incoming.forEach(item => {
          map.set(item.label, item); // replaces if exists, adds if not
        });

        updated.formatted_data = { ...updated.formatted_data, ...Object.fromEntries(map.entries()) };
        updated.extracted_table_rows = allTableRows;
        return updated
      })

    } catch (error) {
      console.error("❌ Error parsing PDF:", error);
      alert("Failed to parse PDF");
    } finally {
      setLoading(false);
    }
  };

  return (
    <UploadButton
      loading={loading}
      name={name}
      files={files}
      onChange={handleFileChange}
      setFiles={setFiles}
      className='mb-5'
    />
  );
};

export default PDFExtractorOfLabTest;